import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  Typography,
  Grid,
  Autocomplete,
  Collapse,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  PictureAsPdf,
} from "@mui/icons-material";
import { API_URL } from "../../../config";
import Cookies from "js-cookie";
import CatalogImageManager from "./CatalogImageManager";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "jspdf-autotable";
import Pagination from "../../../Common_Components/Pagination";

const USER_ID = Cookies.get("id");

const Catalog = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openRows, setOpenRows] = useState({});
  const ROLE = Cookies.get("role");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    p_name: "",
    categoryId: "",
    note: "",
    catalog_info: [{ size: "", fabric: "", retail_price: "", dealer_price: "" }],
    images: [],
    imagePreviews: [],
  });
  const userId = Cookies.get("id");

  // ---------------- Fetch Data ----------------
  const fetchCatalogs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/getCatalogs`, {
        params: {
          userId: USER_ID,
          page,
          limit,
          search,
          sortBy,
          sortOrder,
          categoryId: selectedCategoryId,
        },
      });

      setCatalogs(res.data.catalogs || []);
      setTotalEntries(res.data.total || 0);
      setTotalPages(res.data.totalPages || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch catalogs");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async (searchText = "") => {
    try {
      const res = await axios.get(`${API_URL}/getAllCategories`, {
        params: {
          userId: USER_ID,
          search: searchText, // IMPORTANT: backend receives search for filtering
          page: 1,
          limit: 20,
        },
      });
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch categories");
    }
  };

  useEffect(() => {
    fetchCatalogs();
    fetchCategories();
  }, [page, limit, search, sortBy, sortOrder, selectedCategoryId]);

  // ---------------- Handlers ----------------
  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === "asc";
    setSortBy(field);
    setSortOrder(isAsc ? "desc" : "asc");
  };

  const handleOpenModal = (catalog = null) => {
    if (catalog) {
      setIsEditing(true);
      setEditId(catalog._id);
      setFormData({
        p_name: catalog.p_name,
        categoryId: catalog.categoryId?._id || "",
        p_code: catalog?.p_code || "",
        note: catalog.note || "",
        catalog_info:
          catalog.catalog_info?.length > 0
            ? catalog.catalog_info
            : [{ size: "", fabric: "", retail_price: "", dealer_price: "" }],
        images: [],
        imagePreviews: catalog.images?.map((img) => img) || [],
      });
    } else {
      setIsEditing(false);
      setEditId(null);
      setFormData({
        p_name: "",
        p_code: "",
        categoryId: "",
        note: "",
        catalog_info: [{ size: "", fabric: "", retail_price: "", dealer_price: "" }],
        images: [],
        imagePreviews: [],
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleCatalogInfoChange = (index, field, value) => {
    const newInfo = [...formData.catalog_info];
    newInfo[index][field] = value;
    setFormData({ ...formData, catalog_info: newInfo });
  };

  const addCatalogInfoRow = () => {
    setFormData({
      ...formData,
      catalog_info: [
        ...formData.catalog_info,
        { size: "", fabric: "", retail_price: "", dealer_price: "" },
      ],
    });
  };

  const removeCatalogInfoRow = (index) => {
    const newInfo = [...formData.catalog_info];
    newInfo.splice(index, 1);
    setFormData({ ...formData, catalog_info: newInfo });
  };

  const handleImageChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...newFiles],
      imagePreviews: [...prev.imagePreviews, ...newPreviews],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        // Prepare JSON payload
        const payload = {
          p_name: formData.p_name,
          p_code: formData.p_code,
          userId: USER_ID,
          categoryId: formData.categoryId,
          note: formData.note,
          catalog_info: formData.catalog_info,
        };

        await axios.put(`${API_URL}/updateCatalog/${editId}`, payload);
        toast.success("Catalog updated");
      } else {
        // For creation, we still send FormData if images are involved
        const data = new FormData();
        data.append("p_name", formData.p_name);
        data.append("p_code", formData.p_code);
        data.append("userId", USER_ID);
        data.append("categoryId", formData.categoryId);
        data.append("note", formData.note);
        data.append("catalog_info", JSON.stringify(formData.catalog_info));
        formData.images.forEach((img) => data.append("images", img));

        await axios.post(`${API_URL}/createCatalog`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Catalog created");
      }

      handleCloseModal();
      fetchCatalogs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save catalog");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this catalog?"))
      return;
    try {
      await axios.delete(`${API_URL}/deleteCatalog/${id}`, {
        data: { userId },
      });
      toast.success("Catalog deleted");
      fetchCatalogs();
    } catch {
      toast.error("Failed to delete catalog");
    }
  };

  const toggleRow = (id) => {
    setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const generatePDF = async (catalog) => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.height;

    let yPos = 15; // Start position for content

    // === HEADER ===
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(33, 150, 243);
    doc.text(`Catalog: ${catalog.p_name}`, pageWidth / 2, yPos, {
      align: "center",
    });
    yPos += 10; // Adjust Y position for next content

    // === CATEGORY (Left-aligned) ===
    if (catalog.categoryId) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Category: ${catalog.categoryId.name}`, 10, yPos); // Align to the left
      yPos += 8; // Adjust Y position for next content
    }

    // === PRODUCT CODE (Below Category) ===
    if (catalog.p_code) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Product Code: ${catalog.p_code}`, 10, yPos); // Align to the left (same as Category)
      yPos += 8; // Adjust Y position for next content
    }

    // === NOTE ===
    if (catalog.note) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text("Note:", 10, yPos); // Add "Note:" label
      yPos += 6; // Move down a bit after the label
      const noteText = doc.splitTextToSize(catalog.note, pageWidth - 20);
      doc.text(noteText, 10, yPos);
      yPos += noteText.length * 6 + 5; // Adjust for wrapped note text height
    }

    // === DESCRIPTION (Wrapped Text) ===
    if (catalog.description) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      const descText = doc.splitTextToSize(catalog.description, pageWidth - 20);
      doc.text(descText, 10, yPos);
      yPos += descText.length * 6 + 5; // Adjust for wrapped description height
    }

    // === IMAGES SECTION (Only if images exist) ===
    if (catalog.images?.length) {
      const imgSize = 50; // Adjust image size as needed
      let xPos = 10;
      for (let index = 0; index < catalog.images.length; index++) {
        if (yPos + imgSize + 5 > pageHeight - 10) {
          doc.addPage();
          yPos = 10; // Reset Y position for a new page
        }

        const imgUrl = `${API_URL}/uploads/${catalog.images[index].imageName}`;
        try {
          const imgData = await loadImageAsDataURL(imgUrl); // Ensure loadImageAsDataURL is awaited
          doc.addImage(imgData, "JPEG", xPos, yPos, imgSize, imgSize);
        } catch (e) {
          console.warn("Failed to load image:", imgUrl);
        }

        xPos += imgSize + 5;
        if (xPos + imgSize > pageWidth - 10) {
          xPos = 10;
          yPos += imgSize + 5;
        }
      }
      yPos += imgSize + 10; // Adjust Y position after images section
    }

    // === CATALOG INFORMATION TABLE ===
    const catalogInfo = catalog.catalog_info || [];

    const hasRetail = catalogInfo.some(
      (info) => info.retail_price != null && info.retail_price !== ""
    );
    const hasDealer = catalogInfo.some(
      (info) => info.dealer_price != null && info.dealer_price !== ""
    );

    const head = ["Size", "Fabric"];
    if (hasRetail) head.push("Retail Price");
    if (hasDealer) head.push("Dealer Price");

    const body = catalogInfo.map((info) => {
      const row = [info.size || "-", info.fabric || "-"];
      if (hasRetail) row.push(info.retail_price || "-");
      if (hasDealer) row.push(info.dealer_price || "-");
      return row;
    });

    // Add the table
    doc.autoTable({
      startY: yPos,
      head: [head],
      body: body,
      headStyles: { fillColor: [22, 160, 133], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [242, 242, 242] },
      margin: { left: 10, right: 10 },
      theme: "grid",
    });

    // === FOOTER (Page Count) ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 10);
    }

    // Save the PDF
    doc.save(`${catalog.p_name}.pdf`);
  };

  // Helper function to load images as Data URL
  const loadImageAsDataURL = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous"; // Handle CORS
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg"));
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const [loadingPDF, setLoadingPDF] = useState(false);

  const generatePDFFromBackend = async () => {
    // Prevent duplicate requests
    if (loadingPDF) {
      toast.warn("PDF generation already in progress. Please wait...");
      return;
    }

    try {
      setLoadingPDF(true);

      // Build query params
      const params = {};
      if (selectedCategoryId) {
        params.categoryId = selectedCategoryId;
      }

      const response = await axios.get(
        `${API_URL}/getAllCatalogPDF/${userId}`,
        {
          params,
          responseType: "arraybuffer",
          timeout: 600000, // 10 minutes timeout for large PDFs
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      
      // Generate filename based on selected category
      const selectedCategory = categories.find((c) => c._id === selectedCategoryId);
      const filename = selectedCategory 
        ? `catalogs-${selectedCategory.name}.pdf` 
        : "catalogs.pdf";
      
      link.download = filename; // triggers download
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch (err) {
      console.error(err);
      if (err.response?.status === 429) {
        toast.error("Request already processing. Please wait...");
      } else if (err.response?.status === 503) {
        toast.error("Server busy. Please try again in a moment.");
      } else {
        toast.error(err.response?.data?.error || "Failed to generate PDF");
      }
    } finally {
      setLoadingPDF(false);
    }
  };

  const startEntry = totalEntries === 0 ? 0 : (page - 1) * limit + 1;
  const endEntry = Math.min(page * limit, totalEntries);

  // ---------------- UI ----------------
  return (
    <Box sx={{ p: 4 }}>
      {/* <ToastContainer /> */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Manage Catalogs
      </Typography>

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <TextField
          placeholder="Search catalogs..."
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
        />
        {(ROLE === "admin" || ROLE === "factory_m") && (
          <div style={{ display: "flex", gap: "3px" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
            >
              Add Catalog
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={loadingPDF} // Disable while loading
              onClick={generatePDFFromBackend}
              sx={{ position: "relative", minWidth: 180 }}
            >
              {loadingPDF ? (
                <>
                  <span style={{ opacity: 1 }}>Downloading...</span>
                </>
              ) : (
                "Download Catalog PDF"
              )}{" "}
            </Button>
          </div>
        )}
        {/* PDF Generation Button */}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Select
          value={limit}
          size="small"
          onChange={(e) => setLimit(Number(e.target.value))}
        >
          {[5, 10, 25, 50].map((num) => (
            <MenuItem key={num} value={num}>
              {num} entries per page
            </MenuItem>
          ))}
        </Select>
        {/* Category Dropdown */}
        <Autocomplete
          value={categories.find((c) => c._id === selectedCategoryId) || null}
          onChange={(event, newValue) => {
            setSelectedCategoryId(newValue?._id || "");
            setPage(1);
          }}
          onInputChange={(event, newInputValue) => {
            fetchCategories(newInputValue);
          }}
          options={categories}
          getOptionLabel={(option) => option.name || ""}
          renderInput={(params) => <TextField {...params} label="Category" />}
          isOptionEqualToValue={(option, value) => option._id === value?._id}
          sx={{ width: 200 }}
        />

        <Typography variant="body2">
          Showing {startEntry} to {endEntry} of {totalEntries} entries
        </Typography>
      </Box>
      {/* Table */}
      {loading ? (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "p_name"}
                    direction={sortBy === "p_name" ? sortOrder : "asc"}
                    onClick={() => handleSort("p_name")}
                  >
                    Product Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Product Code</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Images</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {catalogs.map((cat) => (
                <React.Fragment key={cat._id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(cat._id)}
                      >
                        {openRows[cat._id] ? (
                          <KeyboardArrowUp />
                        ) : (
                          <KeyboardArrowDown />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell
                      sx={{
                        wordWrap: "break-word", // Ensure text wraps
                        whiteSpace: "normal", // Allow multiline wrapping
                        maxWidth: 200, // Set a maximum width for the description
                      }}
                    >
                      {cat.p_name}
                    </TableCell>
                    <TableCell
                      sx={{
                        wordWrap: "break-word", // Ensure text wraps
                        whiteSpace: "normal", // Allow multiline wrapping
                        maxWidth: 200, // Set a maximum width for the description
                      }}
                    >
                      {cat.p_code}
                    </TableCell>
                    <TableCell
                      sx={{
                        wordWrap: "break-word", // Ensure text wraps
                        whiteSpace: "normal", // Allow multiline wrapping
                        maxWidth: 200, // Set a maximum width for the description
                      }}
                    >
                      {cat.categoryId?.name || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={{
                        wordWrap: "break-word", // Ensure text wraps
                        whiteSpace: "normal", // Allow multiline wrapping
                        maxWidth: 200, // Set a maximum width for the description
                      }}
                    >
                      {cat.note || "—"}
                    </TableCell>
                    <TableCell>
                      <Grid container spacing={1}>
                        {cat.images?.slice(0, 3).map((img, i) => (
                          <Grid item key={i}>
                            <img
                              src={`${API_URL}/uploads/${img.imageName}`}
                              alt=""
                              width={50}
                              height={50}
                              style={{
                                objectFit: "cover",
                                borderRadius: 4,
                                border: "1px solid #ddd",
                              }}
                            />
                          </Grid>
                        ))}
                        {cat.images?.length > 3 && (
                          <Typography variant="caption" color="text.secondary">
                            +{cat.images.length - 3} more
                          </Typography>
                        )}
                      </Grid>
                    </TableCell>
                    <TableCell>
                      {(ROLE === "admin" || ROLE === "factory_m") && (
                        <>
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenModal(cat)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(cat._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                      <IconButton
                        color="secondary"
                        onClick={() => generatePDF(cat)}
                        title="Download PDF"
                      >
                        <PictureAsPdf />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row */}
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, border: "none" }}>
                      <Collapse
                        in={openRows[cat._id]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ margin: 2 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ mb: 1, fontWeight: 600 }}
                          >
                            Catalog Info:
                          </Typography>
                          <Grid container spacing={2}>
                            {cat.catalog_info?.length > 0 ? (
                              cat.catalog_info.map((info, idx) => (
                                <Grid item xs={12} sm={6} md={4} key={idx}>
                                  <Paper
                                    elevation={1}
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 1,
                                      bgcolor: "#f9f9f9",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      <b>Size:</b> {info.size || "—"}
                                    </Typography>
                                    <Typography variant="body2">
                                      <b>Fabric:</b> {info.fabric || "—"}
                                    </Typography>
                                    {info.retail_price !== null && (
                                      <Typography variant="body2">
                                        <b>Retail Price:</b>{" "}
                                        {info.retail_price || "—"}
                                      </Typography>
                                    )}
                                    {info.dealer_price !== null && (
                                      <Typography variant="body2">
                                        <b>Dealer Price:</b>{" "}
                                        {info.dealer_price || "—"}
                                      </Typography>
                                    )}
                                  </Paper>
                                </Grid>
                              ))
                            ) : (
                              <Typography variant="body2" sx={{ pl: 1 }}>
                                No catalog info available.
                              </Typography>
                            )}
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}

              {catalogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    No catalogs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Pagination */}
      {totalPages >= 1 && (
        <Pagination page={page} setPage={setPage} totalPages={totalPages} />
      )}

      {/* Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{isEditing ? "Edit Catalog" : "Add Catalog"}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              label="Product Name"
              variant="outlined"
              required
              value={formData.p_name}
              onChange={(e) =>
                setFormData({ ...formData, p_name: e.target.value })
              }
            />

            <TextField
              label="Product Code"
              variant="outlined"
              required
              value={formData.p_code}
              onChange={(e) =>
                setFormData({ ...formData, p_code: e.target.value })
              }
            />

            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.name || ""}
              value={
                categories.find((c) => c._id === formData.categoryId) || null
              }
              onChange={(e, newValue) =>
                setFormData({ ...formData, categoryId: newValue?._id || "" })
              }
              isOptionEqualToValue={(o, v) => o._id === v._id}
              size="small"
              renderInput={(params) => (
                <TextField {...params} label="Select Category" />
              )}
            />

            <TextField
              label="Note"
              multiline
              rows={2}
              value={formData.note}
              onChange={(e) =>
                setFormData({ ...formData, note: e.target.value })
              }
            />

            {/* Images */}
            {isEditing ? (
              <CatalogImageManager
                catalogId={editId}
                images={formData.imagePreviews}
                onImagesUpdated={(newImages) =>
                  setFormData({ ...formData, imagePreviews: newImages })
                }
              />
            ) : (
              <>
                <Button variant="contained" component="label">
                  Upload Images
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={handleImageChange}
                  />
                </Button>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {formData.imagePreviews.map((src, idx) => (
                    <Grid item key={idx}>
                      <Box
                        sx={{
                          position: "relative",
                          width: 80,
                          height: 80,
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "1px solid #ccc",
                        }}
                      >
                        <img
                          src={src}
                          alt={`preview-${idx}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            bgcolor: "rgba(255,255,255,0.7)",
                          }}
                          onClick={() => {
                            const newImages = [...formData.images];
                            const newPreviews = [...formData.imagePreviews];
                            newImages.splice(idx, 1);
                            newPreviews.splice(idx, 1);
                            setFormData({
                              ...formData,
                              images: newImages,
                              imagePreviews: newPreviews,
                            });
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* Catalog Info */}
            <Typography variant="h6">Catalog Info</Typography>
            {formData.catalog_info.map((info, idx) => (
              <Grid container spacing={2} key={idx}>
                <Grid item xs={3}>
                  <TextField
                    label="Size"
                    value={info.size}
                    onChange={(e) =>
                      handleCatalogInfoChange(idx, "size", e.target.value)
                    }
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Fabric"
                    value={info.fabric}
                    onChange={(e) =>
                      handleCatalogInfoChange(idx, "fabric", e.target.value)
                    }
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Retail Price"
                    type="number"
                    value={info.retail_price}
                    onChange={(e) =>
                      handleCatalogInfoChange(
                        idx,
                        "retail_price",
                        e.target.value
                      )
                    }
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Dealer Price"
                    type="number"
                    value={info.dealer_price}
                    onChange={(e) =>
                      handleCatalogInfoChange(
                        idx,
                        "dealer_price",
                        e.target.value
                      )
                    }
                    fullWidth
                    required
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Button
                    color="error"
                    onClick={() => removeCatalogInfoRow(idx)}
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
            ))}

            <Button variant="outlined" onClick={addCatalogInfoRow}>
              Add More Info
            </Button>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" variant="contained">
              {isEditing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Catalog;
